import { useCallback, useState } from 'react'
import { createId } from '@util'
import {
  isDefined,
  isNotDefined,
  type DashboardDataWithEditable,
  type DashboardListInput,
  type DashboardScopeId,
  type UserProfileData,
} from '@terros/common'
import { useNavigate } from '@tanstack/react-router'
import { useToastError } from '@hooks'
import { getDashboardAudienceKinds, getDashboardManageTier, useAddDashboard } from '@front/model'

type Props = {
  listInput: DashboardListInput | undefined
  user: UserProfileData | undefined
}

type DashboardCreateState = {
  /** The dashboard being duplicated, or `undefined` when creating from scratch. */
  duplicateSource: DashboardDataWithEditable | undefined
  isPickerOpen: boolean
  isSubmitting: boolean
  openCreate: () => void
  openDuplicate: (dashboard: DashboardDataWithEditable) => void
  closePicker: () => void
  /** Creates the dashboard for the chosen audience and opens its build screen. */
  submit: (scopeId: DashboardScopeId) => Promise<void>
}

/**
 * Create and duplicate both resolve an audience first, then write the dashboard
 * and land on its build screen. The dashboard exists from that point on, so
 * abandoning the build screen leaves an empty dashboard behind.
 *
 * A viewer whose only possible audience is themselves skips the picker — a modal
 * with one card is a detour, not a choice.
 */
export function useDashboardCreate({ listInput, user }: Props): DashboardCreateState {
  const navigate = useNavigate()
  const { add, isAdding, error } = useAddDashboard(listInput)
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [duplicateSource, setDuplicateSource] = useState<DashboardDataWithEditable | undefined>(undefined)
  useToastError(error)

  const closePicker = useCallback((): void => {
    setIsPickerOpen(false)
    setDuplicateSource(undefined)
  }, [])

  const createForAudience = useCallback(
    async (scopeId: DashboardScopeId, source: DashboardDataWithEditable | undefined): Promise<void> => {
      // Only the mutation failure is swallowed (the toast covers it) — anything
      // past it should surface rather than vanish into the same catch.
      let dashboard: DashboardDataWithEditable | undefined
      try {
        dashboard = await add({
          unsaved: {
            name: source?.name,
            dateRange: source?.dateRange,
            filter: source?.filter,
            scopeId,
            // Fresh widget ids: a duplicate is a separate dashboard, and reusing
            // ids would make the two dashboards' widgets indistinguishable.
            widgets: (source?.widgets ?? []).map((widget) => ({
              ...widget,
              widgetId: createId('DashWidget'),
            })),
          },
        })
      } catch {
        return
      }
      closePicker()
      if (isNotDefined(dashboard)) return
      await navigate({ to: `/dashboard/${dashboard.dashboardId}/edit` })
    },
    [add, closePicker, navigate]
  )

  const submit = useCallback(
    (scopeId: DashboardScopeId): Promise<void> => createForAudience(scopeId, duplicateSource),
    [createForAudience, duplicateSource]
  )

  const canOnlyBuildForSelf = getDashboardAudienceKinds(getDashboardManageTier(user?.permissionMap)).length === 1

  const openCreate = useCallback((): void => {
    setDuplicateSource(undefined)
    if (canOnlyBuildForSelf && isDefined(user?.userId)) {
      void createForAudience(user.userId, undefined)
      return
    }
    setIsPickerOpen(true)
  }, [canOnlyBuildForSelf, createForAudience, user?.userId])

  const openDuplicate = useCallback(
    (dashboard: DashboardDataWithEditable): void => {
      setDuplicateSource(dashboard)
      if (canOnlyBuildForSelf && isDefined(user?.userId)) {
        void createForAudience(user.userId, dashboard)
        return
      }
      setIsPickerOpen(true)
    },
    [canOnlyBuildForSelf, createForAudience, user?.userId]
  )

  return {
    duplicateSource,
    isPickerOpen,
    isSubmitting: isAdding,
    openCreate,
    openDuplicate,
    closePicker,
    submit,
  }
}
